import { ArrowRight, PartyPopper, Phone, Target } from "lucide-react";
import type { Identifier } from "ra-core";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";

import {
  bucketTitle,
  missionName,
  progressStage,
  rankingLine,
  sortMissions,
  type CallMission,
  type RankingRow,
  MY_DAY_PATH,
} from "./callPlan";
import { currentCallSlot } from "../useAgents";
import { MissionDetails } from "./MissionRow";

const ProgressRing = ({ value, goal }: { value: number; goal: number }) => {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(1, goal ? value / goal : 0);
  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          strokeWidth="8"
          className="stroke-white/70"
        />
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          className="stroke-neutral-900 transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold leading-none">{value}</span>
        <span className="text-[11px] text-neutral-600">de {goal}</span>
      </div>
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div className="flex flex-col rounded-2xl bg-white/60 px-3 py-2">
    <span className="text-lg font-semibold leading-tight">{value}</span>
    <span className="text-xs text-neutral-600">{label}</span>
  </div>
);

/** Inicio: my progress today and the way into my own tab to work the list. */
export const MyDayCard = ({
  missions,
  ranking,
  meId,
}: {
  missions: CallMission[];
  ranking: RankingRow[];
  meId?: Identifier;
}) => {
  const mine = sortMissions(
    missions.filter((mission) => String(mission.sales_id) === String(meId)),
    currentCallSlot(),
  );
  const open = mine.filter((mission) => !mission.done_at);
  const next = open[0];
  const row = ranking.find((item) => String(item.id) === String(meId));
  const goal = row?.goal ?? 25;
  const calls = row?.today_calls ?? 0;
  const position = rankingLine(ranking, meId);

  return (
    <section className="flex h-full flex-col gap-4 rounded-3xl bg-lime-200 p-5 text-neutral-900">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Mi jornada</h2>
          </div>
          {calls >= goal ? (
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-neutral-900 px-2.5 py-1 text-xs font-medium text-lime-300">
              <PartyPopper className="h-3.5 w-3.5" />
              Objetivo de hoy cumplido
            </span>
          ) : (
            <span className="text-sm text-neutral-700">
              {progressStage(calls, goal)} · te faltan{" "}
              <strong>{goal - calls}</strong> llamadas
            </span>
          )}
          {position ? (
            <span className="text-sm text-neutral-700">{position}</span>
          ) : null}
        </div>
        <ProgressRing value={calls} goal={goal} />
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Stat label="Llamadas hoy" value={calls} />
        <Stat label="Hablados hoy" value={row?.today_contacts ?? 0} />
        <Stat label="Ventas hoy" value={row?.today_sales ?? 0} />
        <Stat label="Puntos hoy" value={row?.today_points ?? 0} />
      </div>

      {next ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-neutral-900 dark:text-neutral-50">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Siguiente · {bucketTitle(next.bucket)}
            </span>
            <span className="truncate text-lg font-semibold">
              {missionName(next)}
            </span>
            <MissionDetails mission={next} />
          </div>
          {next.phone ? (
            <a
              href={`tel:${next.phone}`}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-lime-300 dark:text-neutral-900 dark:hover:bg-lime-200"
              title={`Llamar ${next.phone}`}
            >
              <Phone className="h-4 w-4" />
            </a>
          ) : null}
        </div>
      ) : (
        <div className="rounded-2xl bg-white/60 p-4 text-sm">
          Tu lista de hoy está vacía: coge faena en «Recomendado ahora».
        </div>
      )}

      <Button
        asChild
        className="mt-auto h-11 rounded-full bg-neutral-900 text-white hover:bg-neutral-800"
      >
        <Link to={MY_DAY_PATH}>
          {open.length
            ? `Ir a mi jornada · ${open.length} en cola`
            : "Ir a mi jornada"}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </section>
  );
};
